/**
 * ptキャンペーンの上部の帯
 * 予告中・開催中のキャンペーンを出し、押すと景品画面または企画一覧へ移ります。
 * 帯に収まらない掲示と本文は、横のボタンから開くポップアップで全件読めます。
 * ログイン後の画面だけが ScreenLayout の topSlot へ渡します。一度もサーバー時刻が分かっていない間は何も出しません。
 * 復帰して取り直す間は、画面が動かないよう最後に分かった時刻で帯を残し、残り時間だけ隠します。
 */

import React, { useRef, useState } from 'react';
import { VISITOR_ROUTES } from '../../../navigation/routeNames';
import { Modal, Pressable, ScrollView, Text, View, useWindowDimensions } from '../../../shared/components/WebPrimitives';
import usePointCampaignBoard from '../../../shared/hooks/usePointCampaignBoard';
import useServerNowMs from '../../../shared/hooks/useServerNowMs';
import {
  formatPointCampaignRemaining,
  formatPointCampaignStartsIn,
  selectPointCampaignBandEntries,
  type PointCampaignBandEntry,
} from '../utils/pointCampaignBand';

/** 2件まで・本文の行まで出す画面の高さの下限。これより低い画面では画面の本文の高さを残すため、1件を見出しと残り時間だけで出す */
const BAND_ROOMY_MIN_HEIGHT_PX = 700;

/** 帯の枠と背景 */
const BAND_ROW_CLASS_NAME = 'flex-row items-center gap-2 text-left border-2 border-app-quest-night bg-[#FFE9A8] px-2 py-1 shadow-[2px_2px_0_rgba(22,36,58,0.34)]';

/** キャンペーンの側ごとの表示と移動先 */
const SIDE_DISPLAY = Object.freeze({
  spend: { label: '景品', routeName: VISITOR_ROUTES.REWARDS, destination: '景品一覧' },
  earn: { label: '企画', routeName: VISITOR_ROUTES.EVENTS, destination: '企画一覧' },
});

/** 帯のプロパティ */
type PointCampaignBandProps = {
  navigation: { navigate: (routeName: string) => void };
  /** 表示中の画面のルート名。移動先の画面では押せる形にしない */
  activeRouteName?: string;
};

/**
 * 残り時間などの文言を作る
 * @param {PointCampaignBandEntry} entry - 掲示
 * @param {number | null} nowMs - サーバー時刻。取り直している間はnull
 * @returns {string | null} 文言。時刻を取り直している間はnull
 */
const resolveTimeLabel = (entry: PointCampaignBandEntry, nowMs: number | null): string | null => {
  if (nowMs === null) return null;
  return entry.phase === 'active'
    ? formatPointCampaignRemaining(entry.item.endsAtMs - nowMs)
    : formatPointCampaignStartsIn(entry.item.startsAtMs - nowMs);
};

/**
 * 段階の札
 * @param {Object} props - プロパティ
 * @param {PointCampaignBandEntry['phase']} props.phase - 段階
 * @returns {JSX.Element} 段階の札
 */
const PointCampaignPhaseBadge = ({ phase }: { phase: PointCampaignBandEntry['phase'] }): React.ReactElement => (
  <View className={`shrink-0 border-2 border-app-quest-night px-1 py-px ${phase === 'active' ? 'bg-app-quest-night' : 'bg-white'}`}>
    <Text className={`text-[11px] font-extrabold ${phase === 'active' ? 'text-white' : 'text-app-quest-night'}`}>
      {phase === 'active' ? '開催中' : '予告'}
    </Text>
  </View>
);

/**
 * 帯の1行の中身
 * @param {Object} props - プロパティ
 * @param {PointCampaignBandEntry} props.entry - 掲示
 * @param {string | null} props.timeLabel - 残り時間などの文言。時刻を取り直している間はnull
 * @param {boolean} props.showBody - 本文を出すか
 * @returns {JSX.Element} 帯の1行の中身
 */
const PointCampaignBandRowContent = ({ entry, timeLabel, showBody }: {
  entry: PointCampaignBandEntry;
  timeLabel: string | null;
  showBody: boolean;
}): React.ReactElement => (
  <>
    <PointCampaignPhaseBadge phase={entry.phase} />
    <View className="min-w-0 flex-1">
      <Text className="line-clamp-2 break-words text-sm font-extrabold text-app-quest-night">{entry.item.title}</Text>
      <Text className="break-words text-xs font-bold text-app-quest-night">
        {`${SIDE_DISPLAY[entry.item.side].label}${timeLabel ? `・${timeLabel}` : ''}`}
      </Text>
      {showBody && entry.item.body ? (
        <Text className="line-clamp-1 break-words text-xs text-app-text">{entry.item.body}</Text>
      ) : null}
    </View>
  </>
);

/**
 * 全件の見出しと本文を省かず読むポップアップ
 * @param {Object} props - プロパティ
 * @param {boolean} props.visible - 表示するか
 * @param {PointCampaignBandEntry[]} props.entries - 帯に出せる全掲示
 * @param {number | null} props.nowMs - サーバー時刻。取り直している間はnull
 * @param {string} [props.activeRouteName] - 表示中の画面のルート名。その画面へ移るボタンは出さない
 * @param {(routeName: string) => void} props.onOpenRoute - 移動先を開く
 * @param {() => void} props.onClose - 閉じる
 * @returns {JSX.Element} ポップアップ
 */
const PointCampaignListModal = ({ visible, entries, nowMs, activeRouteName, onOpenRoute, onClose }: {
  visible: boolean;
  entries: PointCampaignBandEntry[];
  nowMs: number | null;
  activeRouteName?: string;
  onOpenRoute: (routeName: string) => void;
  onClose: () => void;
}): React.ReactElement => (
  <Modal
    accessibilityLabel="ptキャンペーンの一覧"
    onRequestClose={onClose}
    testID="point-campaign-list-dialog"
    transparent
    visible={visible}
  >
    <View className="flex-1 items-center justify-center bg-black/70 px-2 py-3">
      <View className="flex max-h-[calc(100dvh-24px)] min-h-0 w-full max-w-[480px] flex-col overflow-hidden border-4 border-app-quest-night bg-[#FFF8DD] shadow-[6px_6px_0_rgba(22,36,58,0.34)]">
        <View className="shrink-0 flex-row items-center justify-between gap-2 bg-app-quest-night px-3 py-2">
          <Text className="min-w-0 flex-1 break-words text-base font-extrabold text-white" role="heading">
            開催中・予告のptキャンペーン
          </Text>
          <Text className="shrink-0 text-sm font-extrabold text-app-quest-gold">{`全${entries.length}件`}</Text>
        </View>

        <ScrollView className="min-h-0 flex-1" contentContainerClassName="gap-2 p-3">
          {entries.length === 0 ? (
            <Text className="text-sm text-app-text">開催中・予告のキャンペーンはありません。</Text>
          ) : null}
          {entries.map((entry) => {
            /** 側ごとの表示と移動先 */
            const sideDisplay = SIDE_DISPLAY[entry.item.side];
            /** 残り時間などの文言 */
            const timeLabel = resolveTimeLabel(entry, nowMs);
            return (
              <View className="gap-1 border-2 border-app-quest-night bg-white p-2" key={entry.item.id}>
                <View className="flex-row items-center gap-2">
                  <PointCampaignPhaseBadge phase={entry.phase} />
                  <Text className="break-words text-xs font-bold text-app-quest-night">
                    {`${sideDisplay.label}${timeLabel ? `・${timeLabel}` : ''}`}
                  </Text>
                </View>
                <Text className="break-words text-base font-extrabold text-app-quest-night">{entry.item.title}</Text>
                {entry.item.body ? (
                  <Text className="whitespace-pre-wrap break-words text-sm leading-6 text-app-text">{entry.item.body}</Text>
                ) : null}
                {sideDisplay.routeName === activeRouteName ? null : (
                  <Pressable
                    accessibilityLabel={`${sideDisplay.destination}を開く: ${entry.item.title}`}
                    accessibilityRole="button"
                    className="mt-1 min-h-11 items-center justify-center border-2 border-app-quest-night bg-[#FFE9A8] px-3 py-2 active:translate-x-[1px] active:translate-y-[1px]"
                    onPress={() => onOpenRoute(sideDisplay.routeName)}
                  >
                    <Text className="text-sm font-extrabold text-app-quest-night">{`${sideDisplay.destination}を開く ›`}</Text>
                  </Pressable>
                )}
              </View>
            );
          })}
        </ScrollView>

        <View className="shrink-0 border-t-2 border-app-quest-night p-2">
          <Pressable
            accessibilityRole="button"
            className="min-h-11 items-center justify-center border-2 border-app-quest-night bg-app-quest-night px-3 py-2 active:translate-x-[1px] active:translate-y-[1px]"
            onPress={onClose}
          >
            <Text className="text-sm font-extrabold text-white">閉じる</Text>
          </Pressable>
        </View>
      </View>
    </View>
  </Modal>
);

/**
 * ptキャンペーンの上部の帯
 * @param {PointCampaignBandProps} props - プロパティ
 * @returns {JSX.Element | null} 帯。出す掲示が無く、ポップアップも開いていなければnull
 */
const PointCampaignBand = ({ navigation, activeRouteName }: PointCampaignBandProps): React.ReactElement | null => {
  /** 全画面共通の掲示 */
  const { items } = usePointCampaignBoard();
  /** サーバー時刻 */
  const nowMs = useServerNowMs();
  /** 表示領域の高さ */
  const { height } = useWindowDimensions();
  /** 全件のポップアップを開いているか */
  const [isListOpen, setIsListOpen] = useState(false);
  /** 最後に分かったサーバー時刻 */
  const lastKnownNowMsRef = useRef<number | null>(null);
  if (nowMs !== null) lastKnownNowMsRef.current = nowMs;
  /** 帯の判定に使う時刻 */
  const displayNowMs = nowMs ?? lastKnownNowMsRef.current;

  if (displayNowMs === null) return null;

  /** 帯に出せる掲示 */
  const entries = selectPointCampaignBandEntries(items, displayNowMs);
  // 読んでいる最中に全件が終わってもポップアップを急に消さない
  if (entries.length === 0 && !isListOpen) return null;

  /** 実際に出す掲示 */
  const visibleEntries = entries.slice(0, height >= BAND_ROOMY_MIN_HEIGHT_PX ? 2 : 1);
  /** 出しきれなかった件数 */
  const hiddenCount = entries.length - visibleEntries.length;
  /** 本文の行を出すか。2件並ぶ時と高さの低い画面は省く（読み上げ名には全文を入れる） */
  const showBody = height >= BAND_ROOMY_MIN_HEIGHT_PX && visibleEntries.length === 1;
  /** 全件を開くボタンの2行の文言 */
  const listButtonLines = hiddenCount > 0 ? [`ほか${hiddenCount}件`, 'を見る'] : ['詳しく', '見る'];

  return (
    <>
      {entries.length > 0 ? (
        <View accessibilityLabel="ptキャンペーンのお知らせ" className="mb-2 shrink-0 flex-row items-stretch gap-1" role="region" testID="point-campaign-band">
          <View className="min-w-0 flex-1 gap-1">
            {visibleEntries.map((entry) => {
              /** 残り時間などの文言 */
              const timeLabel = resolveTimeLabel(entry, nowMs);
              /** 側ごとの表示と移動先 */
              const sideDisplay = SIDE_DISPLAY[entry.item.side];
              /** 行の中身 */
              const content = (
                <PointCampaignBandRowContent
                  entry={entry}
                  showBody={showBody}
                  timeLabel={timeLabel}
                />
              );
              return sideDisplay.routeName === activeRouteName ? (
                <View className={BAND_ROW_CLASS_NAME} key={entry.item.id}>{content}</View>
              ) : (
                <Pressable
                  accessibilityHint={`${sideDisplay.destination}を開きます`}
                  accessibilityLabel={`${entry.phase === 'active' ? '開催中' : '予告'} ${entry.item.title}。${timeLabel ? `${timeLabel}。` : ''}${entry.item.body}`}
                  accessibilityRole="button"
                  className={`min-h-11 ${BAND_ROW_CLASS_NAME} active:translate-x-[1px] active:translate-y-[1px] active:shadow-none`}
                  key={entry.item.id}
                  onPress={() => navigation.navigate(sideDisplay.routeName)}
                >
                  {content}
                  <Text accessible={false} className="shrink-0 text-base font-extrabold text-app-quest-night">›</Text>
                </Pressable>
              );
            })}
          </View>
          <Pressable
            accessibilityLabel={`${listButtonLines.join('')}（ptキャンペーン全${entries.length}件）`}
            accessibilityRole="button"
            className="min-h-11 w-16 shrink-0 items-center justify-center border-2 border-app-quest-night bg-app-quest-night px-0.5 py-1 shadow-[2px_2px_0_rgba(22,36,58,0.34)] active:translate-x-[1px] active:translate-y-[1px] active:shadow-none"
            onPress={() => setIsListOpen(true)}
            testID="point-campaign-band-list-button"
          >
            {listButtonLines.map((line) => (
              <Text accessible={false} className="text-center text-xs font-extrabold text-white" key={line}>{line}</Text>
            ))}
          </Pressable>
        </View>
      ) : null}
      <PointCampaignListModal
        activeRouteName={activeRouteName}
        entries={entries}
        nowMs={nowMs}
        onClose={() => setIsListOpen(false)}
        onOpenRoute={(routeName) => {
          setIsListOpen(false);
          navigation.navigate(routeName);
        }}
        visible={isListOpen}
      />
    </>
  );
};

export default PointCampaignBand;
